import React, { createContext, useContext } from 'react';
import { heatmapAPI, trajectoryAPI, vipAPI, vipPrivateAPI } from '../services/api';

// VipSourceContext tells the VIP pages (dashboard, player profile, player lookup)
// which data they show. The default is the VIP dashboard as it has always been. The private VIP
// dashboard wraps the same pages in PRIVATE_VIP_SOURCE: its own API (one agent's book, fixed on
// the server), its own paths, and the sections it leaves out.

export const LIVE_VIP_SOURCE = Object.freeze({
  isPrivate: false,
  // fixedAgent replaces the agent picked in the top bar; null = use the picked one.
  fixedAgent: null,
  // canIssueBonus shows the profile's "Issue bonus" form (bi_backoffice.temp_vip_bonuses_hist).
  canIssueBonus: false,
  title: 'VIP Dashboard',
  paths: Object.freeze({
    dashboard: '/vip-dashboard',
    player: '/player',
  }),
  api: Object.freeze({
    getDashboard: vipAPI.getDashboard,
    searchPlayers: vipAPI.searchPlayers,
    getPlayerStats: vipAPI.getPlayerStats,
    getPlayerStatsRange: vipAPI.getPlayerStatsRange,
    getPlayerGames: vipAPI.getPlayerGames,
    getPobaHistory: vipAPI.getPobaHistory,
    getHeatmapPlayer: heatmapAPI.getPlayer,
    getTrajectoryPlayer: trajectoryAPI.getPlayer,
  }),
  // Sections a source leaves out: poba (PoBA/CLTV), comps (comp signal), affiliates, contacts
  // (contact log), insights (AI insights), birthdays (the dashboard's upcoming-birthdays panel).
  hidden: Object.freeze({}),
});

export const PRIVATE_VIP_SOURCE = Object.freeze({
  isPrivate: true,
  fixedAgent: 'private',
  canIssueBonus: true,
  title: 'My VIPs',
  paths: Object.freeze({
    dashboard: '/vip-private',
    player: '/vip-private/player',
    bonuses: '/vip-private/bonuses',
  }),
  api: Object.freeze({
    // The server fixes the agent; the signatures match LIVE_VIP_SOURCE so the pages need not care.
    getDashboard: (_agent, params) => vipPrivateAPI.getDashboard(params),
    searchPlayers: vipPrivateAPI.searchPlayers,
    getPlayerStats: vipPrivateAPI.getPlayerStats,
    getPlayerStatsRange: vipPrivateAPI.getPlayerStatsRange,
    getPlayerGames: vipPrivateAPI.getPlayerGames,
    getPobaHistory: null,
    getHeatmapPlayer: vipPrivateAPI.getHeatmapPlayer,
    getTrajectoryPlayer: vipPrivateAPI.getTrajectoryPlayer,
  }),
  hidden: Object.freeze({ poba: true, comps: true, affiliates: true, contacts: true, insights: true, birthdays: true }),
});

const VipSourceContext = createContext(LIVE_VIP_SOURCE);

export function VipSourceProvider({ source, children }) {
  return <VipSourceContext.Provider value={source}>{children}</VipSourceContext.Provider>;
}

export function useVipSource() {
  return useContext(VipSourceContext);
}
